"use client";
import * as React from "react";
import {
  Typography, Button, TextField, Stack, FormHelperText,
} from "@mui/material";
import { WorkspaceCard } from "../Common/WorkspaceCard";

const MAX_BYTES = 8 * 1024;

interface Props {
  projectId: string;
  initialInstructions: string;
  onSave: (text: string) => Promise<void>;
}

export function ProjectInstructionsEditor({ projectId, initialInstructions, onSave }: Props) {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState(initialInstructions);
  const tooLong = new TextEncoder().encode(text).length > MAX_BYTES;

  if (!editing) {
    return (
      <WorkspaceCard interactive={false} data-ph-scope="instructions">
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Instructions</Typography>
          <Button data-ph="edit" onClick={() => setEditing(true)}>Edit</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          Artie follows these in every chat started in this project.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          {initialInstructions || <em>No instructions set.</em>}
        </Typography>
      </WorkspaceCard>
    );
  }

  return (
    <WorkspaceCard interactive={false} data-ph-scope="instructions">
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Instructions</Typography>
      <TextField
        multiline
        minRows={4}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        error={tooLong}
        placeholder="Describe how Artie should behave in this project — tone, role, what to focus on, formats to use…"
      />
      {tooLong && <FormHelperText error>Instructions are too long (max 8 KB).</FormHelperText>}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          data-ph="save"
          disabled={tooLong}
          onClick={async () => {
            if (tooLong) return;
            await onSave(text);
            setEditing(false);
          }}
        >
          Save
        </Button>
        <Button data-ph="cancel" onClick={() => { setText(initialInstructions); setEditing(false); }}>
          Cancel
        </Button>
      </Stack>
    </WorkspaceCard>
  );
}
